import React, { useMemo, useState } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SectionCard from '../../../components/SectionCard';
import StatCard from '../../../components/StatCard';
import StatusChip from '../../../components/StatusChip';
import InvButton from '../../../components/InvButton';
import { InvTextField, InvSelect } from '../../../components/InvField';
import { listBills } from '../../../api/pharmacyDispense';
import { getPharmacyBills } from '../../../api/pharmacy';
import { exportToCsv, formatCurrency, formatDate, parseMoney } from '../../../utils';
import { mapList, mapBill } from '../../../utils/pharmacyMappers';
import usePharmacyLoad from '../../../hooks/usePharmacyLoad';
import { IPD_COLORS } from '../../../theme';
import { fadeInSx, selectedRowSx } from '../pharmacyStyles';
import { PharmacyFilterBar } from '../PharmacyUi';
import PharmacyTableState from '../PharmacyTableState';
import BillDetailDrawer from '../components/BillDetailDrawer';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  ...['PENDING_APPROVAL', 'RECHECKED', 'FINALIZED', 'PAID', 'CANCELLED'].map((s) => ({
    value: s, label: s.replace(/_/g, ' '),
  })),
];

export default function BillsTab() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [receiptTabHint, setReceiptTabHint] = useState(false);

  const { data, loading, error, reload } = usePharmacyLoad(
    async () => {
      try {
        return await listBills(statusFilter ? { status: statusFilter } : {});
      } catch {
        // Fallback to legacy list endpoint while migrating
        return getPharmacyBills(statusFilter ? { status: statusFilter } : undefined);
      }
    },
    [statusFilter],
  );

  const bills = useMemo(() => mapList(data, mapBill).filter((b) => {
    const status = String(b.status || '').toUpperCase();
    return status && status !== 'DRAFT';
  }), [data]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return bills;
    return bills.filter((b) => [
      b.id, b.bill_number, b.patient_name, b.search_mobile, b.dispense_id,
    ].some((v) => String(v).toLowerCase().includes(q)));
  }, [bills, search]);

  const totalAmount = filtered.reduce((s, b) => s + parseMoney(b.amount), 0);
  const paidCount = filtered.filter((b) => b.status === 'PAID').length;

  function openBill(id, preferReceipt = false) {
    setReceiptTabHint(preferReceipt);
    setSelectedId(id);
  }

  return (
    <Box sx={fadeInSx}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 2.5 }}>
        <StatCard label="Bills" value={filtered.length} icon={ReceiptIcon} tone="teal" loading={loading} />
        <StatCard label="Amount payable" value={formatCurrency(totalAmount)} icon={ReceiptIcon} tone="mint" loading={loading} />
        <StatCard label="Paid" value={paidCount} icon={ReceiptIcon} tone="teal" loading={loading} />
      </Box>

      <PharmacyFilterBar>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <InvTextField label="Search bills" value={search} onChange={(e) => setSearch(e.target.value)} />
        </Box>
        <Box sx={{ width: 200 }}>
          <InvSelect
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={STATUS_OPTIONS}
          />
        </Box>
        <InvButton
          variant="outlined"
          tone="teal"
          size="small"
          startIcon={<DownloadIcon />}
          disabled={!filtered.length}
          onClick={() => exportToCsv('pharmacy-bills.csv', filtered)}
        >
          Export
        </InvButton>
      </PharmacyFilterBar>

      <SectionCard title="Bills" subtitle="Full bill lifecycle — open a row for lines, receipt, payment, and refunds">
        <PharmacyTableState
          loading={loading}
          error={error}
          empty={!loading && !error && filtered.length === 0}
          emptyMessage="No billing records found."
          onRetry={reload}
        >
          <Box sx={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${IPD_COLORS.border}` }}>
                  {['Bill no', 'Patient', 'Status', 'Amount payable', 'Collected', 'Refunded', 'Date', 'Receipt'].map((h) => (
                    <th key={h} style={{
                      textAlign: h === 'Receipt' ? 'center' : 'left',
                      padding: '10px 12px',
                      fontSize: 11,
                      color: IPD_COLORS.textSecondary,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((row, index) => (
                  <tr
                    key={row.id || `bill-${index}`}
                    onClick={() => openBill(row.id, false)}
                    style={{ ...selectedRowSx(selectedId === row.id), cursor: 'pointer' }}
                  >
                    <td style={{ padding: '12px', fontWeight: 600, color: IPD_COLORS.primary }}>
                      {row.bill_number || row.id}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <Typography sx={{ fontSize: 13 }}>{row.patient_name || 'Walk-in'}</Typography>
                      {row.search_mobile && (
                        <Typography sx={{ fontSize: 11, color: IPD_COLORS.textSecondary }}>{row.search_mobile}</Typography>
                      )}
                    </td>
                    <td style={{ padding: '12px' }}><StatusChip status={row.status} /></td>
                    <td style={{ padding: '12px', fontWeight: 600 }}>{formatCurrency(row.amount)}</td>
                    <td style={{ padding: '12px' }}>{formatCurrency(row.collected)}</td>
                    <td style={{ padding: '12px' }}>{formatCurrency(row.refunded)}</td>
                    <td style={{ padding: '12px' }}>{formatDate(row.date)}</td>
                    <td style={{ padding: '8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <Tooltip title={row.status === 'PAID' ? 'View receipt' : 'Receipt available after payment'}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={row.status !== 'PAID'}
                            onClick={() => openBill(row.id, true)}
                            sx={{ color: IPD_COLORS.primary }}
                            aria-label="View receipt"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </PharmacyTableState>
      </SectionCard>

      <BillDetailDrawer
        open={!!selectedId}
        billId={selectedId}
        store={null}
        initialTab={receiptTabHint ? 2 : 0}
        onClose={() => {
          setSelectedId(null);
          setReceiptTabHint(false);
        }}
        onChanged={reload}
      />
    </Box>
  );
}
